/**
 * Filter Bar — the inline chip rail plus the 960px filter panel
 * (INVENTORY-DESKTOP §6.1 / §6.2; `design/Filter Bar.dc.html`).
 *
 * ONE PANEL, ONE SHAPE, EVERY SCREEN (2026-08-27, Marcus). The `# TAGS` picker
 * that used to be its own modal (`TagBar.tsx`, deleted) is the last section of
 * this one, and no section is hidden by host any more — the panel used to draw
 * four of its five sections on Discover alone, which made one component read as
 * five different controls. `lib/logic.ts#passFlt` and `selectors.ts#tagLensOf`
 * are the other half of that: every filter, tags included, now actually narrows
 * on every screen rather than being offered and ignored.
 *
 * Over PER-SCREEN state, which is the correction that followed the same day
 * (`store/filterKey.ts`): one panel, one set per host screen. `filterBarVM` reads whichever
 * screen is hosting it — one of the two places in the app allowed to ask that
 * question, because the panel is mounted once by `DesktopApp` and genuinely
 * cannot know its host any other way.
 *
 * SHARED BY FIVE SCREENS: Discover, Tonight, Interests, GOTY and Playtests all
 * render `<FilterBar/>` inline in their control row. It reads the store
 * directly — the same pattern `sheets/FiltersSheet.tsx` uses — via
 * `filterBarVM(s)`, which is `filtersVM` plus the trigger-button state under
 * the names the prototype's `flt` prop used (§6.1). The `flt` object itself is
 * a DC-runtime artifact and is deliberately NOT reproduced: it existed only
 * because `dc-import` could not reach the store.
 *
 * TWO EXPORTS, ON PURPOSE:
 *   <FilterBar/>    the trigger + active-chip rail, rendered INSIDE a screen
 *   <FilterPanel/>  the 960px modal, mounted ONCE from `DesktopApp`
 *
 * The panel is `position:fixed`, and the screens that host the rail are full of
 * `transform`ed hover tiles — a transformed ancestor re-parents a fixed child's
 * containing block, which would clip the overlay to a tile (§10.5 risk 7).
 * Mounting the panel from `DesktopApp` puts it beside the other modals with no
 * transformed ancestor at all, and costs nothing: both components read the same
 * `fltOpen` flag, so the rail's button still opens it.
 *
 * The panel sits at `z-index:70` — UNDER the playtest / What's New modals (80),
 * which is the prototype's own ordering — and paints `rgba(3,3,6,.75)` rather
 * than `--gt-overlay`, aligns to the top with `60px 20px` padding, scrolls the
 * OVERLAY, and has no `max-height`. All of that is expressed as `Modal` props.
 *
 * DEVIATION (§6.2): the prototype's footer `DONE` carries no match count, where
 * mobile's read `DONE · N MATCHES`. Kept as the prototype has it — the count is
 * in the header (`matchTxt`).
 */

import { Avatar } from '../../components/Avatar.tsx';
import { SearchBox } from '../../components/SearchBox.tsx';
import type { Filters, TriState } from '../../lib/types.ts';
import { useQd } from '../../store/index.ts';
import { removeChip } from '../../store/selectors.3c.ts';
import type { TagChip } from '../../store/selectors.ts';
import { filterBarVM } from '../../store/selectors.desktop.ts';
import { Modal } from './Modal.tsx';
import type { CSSProperties } from 'react';

const MONO = 'var(--gt-font-mono)';

/** section headings — pixel 9px `var(--acc)` (mobile used 8px) */
const LABEL: CSSProperties = {
  fontFamily: 'var(--gt-font-pixel)',
  fontSize: 9,
  color: 'var(--acc)',
};

/** the 1px-collapsing segmented option (`margin-left:-1px` + a z-index) */
const seg = (bd: string, clr: string, bg: string, zi: string, pad: string): CSSProperties => ({
  border: '1px solid ' + bd,
  color: clr,
  background: bg,
  padding: pad,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  marginLeft: -1,
  position: 'relative',
  zIndex: Number(zi),
});

/** the five price ticks sit at 1/6 increments of the usable track */
const TICKS = [0.16667, 0.33333, 0.5, 0.66667, 0.83333];
/** the thumb is 18px wide, so the usable track is `100% - 18px` from `9px` */
const trackAt = (frac: string) => 'calc(9px + (100% - 18px) * ' + frac + ')';

// ---------------------------------------------------------------------------
// the inline rail
// ---------------------------------------------------------------------------

/**
 * `+ FILTERS` + one chip per active filter + an inline `CLEAR ALL`.
 * Drop it straight into a screen's control row; it brings no layout of its own
 * beyond `flex-wrap`.
 */
export function FilterBar(): JSX.Element {
  const s = useQd();
  const v = filterBarVM(s);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        fontFamily: MONO,
        fontSize: 12,
      }}
    >
      <div
        onClick={() => s.toggleFltOpen()}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          border: '1px solid ' + v.btnBd,
          color: v.btnClr,
          background: v.btnBg,
          padding: '9px 14px',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {v.btnTxt}
      </div>

      {v.chips.map((c) => (
        <div
          key={c.key}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            border: '1px solid ' + c.bd,
            color: c.clr,
            background: c.bg,
            padding: '8px 11px',
            whiteSpace: 'nowrap',
          }}
        >
          {c.l}
          {/* native `title`, not `data-tip` — the prototype's one exception */}
          <span
            className="d-hov-acc2"
            onClick={() => removeChip(s, c)}
            title="REMOVE"
            style={{ cursor: 'pointer', color: 'var(--gt-text-5)' }}
          >
            ✕
          </span>
        </div>
      ))}

      {v.hasActive ? (
        <div
          className="d-hov-t5"
          onClick={() => s.clearFlt()}
          style={{ color: 'var(--gt-faint)', cursor: 'pointer', padding: '6px 4px' }}
        >
          CLEAR ALL
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// the panel
// ---------------------------------------------------------------------------

/** Mounted once by `DesktopApp` when `s.fltOpen` is true. */
export function FilterPanel(): JSX.Element {
  const s = useQd();
  const v = filterBarVM(s);
  const close = () => s.toggleFltOpen(false);

  /** A tag chip cycles off → include → exclude → off; the panel never removes. */
  const tagChip = (t: TagChip) => (
    <div
      key={t.key}
      onClick={() => s.cycleTag(t.id)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        border: '1px solid ' + t.bd,
        color: t.clr,
        background: t.bg,
        padding: '8px 11px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        fontFamily: MONO,
        fontSize: 11,
      }}
    >
      {t.pre}
      {t.l}
    </div>
  );

  const bubble = (pct: number, txt: string) => (
    <div
      style={{
        position: 'absolute',
        top: 33,
        left: trackAt(pct + ' / 100'),
        transform: 'translateX(-50%)',
        background: 'var(--gt-card)',
        border: '1px solid var(--acc)',
        color: 'var(--acc)',
        fontFamily: MONO,
        fontSize: 11,
        padding: '2px 5px',
        whiteSpace: 'nowrap',
        zIndex: 6,
      }}
    >
      {txt}
    </div>
  );

  return (
    <Modal
      label="Filters"
      width={960}
      zIndex={70}
      overlay="rgba(3,3,6,.75)"
      align="flex-start"
      padding="60px 20px"
      maxHeight={null}
      scrollOverlay
      onClose={close}
    >
      {/* header — pixel 11px title, mono 12px count, boxed ✕ ---------------- */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 18px',
          borderBottom: '1px solid var(--gt-line)',
        }}
      >
        <div style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: 11, color: 'var(--acc)' }}>+ FILTERS</div>
        <div style={{ fontFamily: MONO, fontSize: 12, color: 'var(--gt-faint)' }}>{v.matchTxt}</div>
        <div style={{ flex: 1 }} />
        <div
          onClick={close}
          style={{
            border: '1px solid var(--gt-border-2)',
            color: 'var(--gt-text-5)',
            fontFamily: MONO,
            fontSize: 12,
            padding: '4px 9px',
            cursor: 'pointer',
          }}
        >
          ✕
        </div>
      </div>

      {/* body ------------------------------------------------------------- */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
          padding: 18,
          fontFamily: MONO,
          fontSize: 12,
        }}
      >
        {/* row 1: PLAYERS · PRICE · DISCOUNT, side by side ----------------- */}
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={LABEL}>PLAYERS</div>
            <div style={{ display: 'flex' }}>
              {v.players.map((c) => (
                <div key={c.key} onClick={() => s.setFlt({ p: c.v })} style={seg(c.bd, c.clr, c.bg, c.zi, '8px 12px')}>
                  {c.l}
                </div>
              ))}
            </div>
          </div>

          {/* No host gate any more: `passFlt` applies price on every screen. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={LABEL}>PRICE</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: 'var(--gt-faint)' }}>$0</span>
              <div className="gt-dual">
                <div style={{ position: 'absolute', left: 0, right: 0, top: 15, height: 2, background: 'var(--gt-border-2)' }} />
                {TICKS.map((t) => (
                  <div
                    key={t}
                    style={{
                      position: 'absolute',
                      left: trackAt(t + ' - 1px'),
                      top: 7,
                      width: 2,
                      height: 18,
                      background: 'var(--gt-tick)',
                    }}
                  />
                ))}
                <div
                  style={{
                    position: 'absolute',
                    top: 15,
                    height: 2,
                    background: 'var(--acc)',
                    left: trackAt(v.prLoPct + ' / 100'),
                    width: 'calc((100% - 18px) * ' + v.prWPct + ' / 100)',
                  }}
                />
                {v.prLoBub ? bubble(v.prLoPct, v.prLoTxt) : null}
                {v.prHiBub ? bubble(v.prHiPct, v.prHiTxt) : null}
                <input
                  type="range"
                  min={0}
                  max={60}
                  step={1}
                  value={v.prLo}
                  onChange={(e) => s.setPrLo(Number(e.target.value))}
                  onPointerDown={() => s.setFltPrDrag('lo')}
                  onPointerUp={() => s.setFltPrDrag('')}
                  style={{ zIndex: 3 }}
                />
                <input
                  type="range"
                  min={0}
                  max={60}
                  step={1}
                  value={v.prHi}
                  onChange={(e) => s.setPrHi(Number(e.target.value))}
                  onPointerDown={() => s.setFltPrDrag('hi')}
                  onPointerUp={() => s.setFltPrDrag('')}
                  style={{ zIndex: 4 }}
                />
              </div>
              <span style={{ color: 'var(--gt-faint)' }}>$60+</span>
              <span style={{ color: 'var(--acc)', whiteSpace: 'nowrap' }}>{v.prLabel}</span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={LABEL}>DISCOUNT</div>
            <div style={{ display: 'flex' }}>
              {v.disc.map((c) => (
                <div
                  key={c.key}
                  onClick={() => s.setFlt({ disc: c.v })}
                  style={seg(c.bd, c.clr, c.bg, c.zi, '8px 12px')}
                >
                  {c.l}
                </div>
              ))}
            </div>
          </div>

          {/*
            REVIEWS — a floor on Steam's OWN rung, which is why there is no
            minimum-review-count control beside it: Steam folds the count into
            the rung, so VERY POS+ already means 50+ reviews and OVERWHELMING
            means 500+ (`selectors.ts#filtersVM.rev` has the full argument).
          */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={LABEL}>REVIEWS</div>
            <div style={{ display: 'flex' }}>
              {v.rev.map((c) => (
                <div
                  key={c.key}
                  onClick={() => s.setFlt({ rs: c.v })}
                  style={seg(c.bd, c.clr, c.bg, c.zi, '8px 12px')}
                >
                  {c.l}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* row 2: STATUS — a 130px label column per row -------------------- */}
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 250 }}>
            <div style={LABEL}>STATUS</div>
            {v.status.map((row) => (
              <div key={row.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 130, flex: 'none', color: 'var(--gt-text-5)' }}>{row.l}</span>
                <div style={{ display: 'flex' }}>
                  {row.opts.map((c) => (
                    <div
                      key={c.key}
                      onClick={() =>
                        row.key === 'pt'
                          ? s.setFlt({ pt: c.v as TriState })
                          : s.setStatusFilter(row.key as keyof Filters['st'], c.v)
                      }
                      style={seg(c.bd, c.clr, c.bg, c.zi, '6px 10px')}
                    >
                      {c.l}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/*
          row 2b: OWNED BY — the crew as toggles, AND'd (2026-08-27, Marcus).

          Beside STATUS rather than under TAGS: it is a filter over rows, not a
          picker with a vocabulary, and the `borderTop` below is what separates
          the two halves of this panel.

          The disabled chips carry `data-tip` AND a `NO LINK` / `CHECKING` badge.
          Redundant here, where every pointer can hover — but the same VM feeds
          the phone sheet, where `Tooltip` shows nothing for a touch pointer, and
          one shape across both hosts is the rule this panel exists to keep.
        */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <div style={LABEL}>OWNED BY</div>
            <div style={{ color: 'var(--gt-faint)', fontSize: 11 }}>
              {v.owned.none ? v.owned.noneTxt : v.owned.note}
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {[v.owned.all, ...v.owned.opts].map((o) => (
              <div
                key={o.key}
                data-tip={o.tip}
                onClick={() => {
                  if (o.dis) return;
                  if (o.k === '') s.toggleOwnAll();
                  else s.toggleOwn(o.k);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  border: '1px solid ' + o.bd,
                  color: o.clr,
                  background: o.bg,
                  padding: '6px 10px',
                  // `default`, not `not-allowed`: the chip is not refusing a
                  // legitimate act, it is stating a fact about that member.
                  cursor: o.dis ? 'default' : 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {o.k === '' ? null : (
                  <Avatar
                    size={20}
                    fs={7.5}
                    ini={o.ini}
                    bg={o.hueBg}
                    hasAv={o.hasAv}
                    avBg={o.avBg}
                    style={{ flex: 'none', opacity: o.dis ? 0.4 : 1 }}
                  />
                )}
                {o.l}
                {o.sub ? (
                  <span style={{ color: 'var(--gt-ghost-2)', fontSize: 9.5 }}>· {o.sub}</span>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        {/* row 3: TAGS — the picker, folded in from the retired # TAGS modal */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, borderTop: '1px solid var(--gt-line)', paddingTop: 18 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <div style={LABEL}># BROWSE BY TAG</div>
            <div style={{ color: 'var(--gt-faint)', fontSize: 11 }}>{v.tags.note}</div>
            <div style={{ flex: 1 }} />
            {/*
              What a pick DOES differs by host, and saying so is cheaper than
              letting someone discover it: Discover re-asks Steam, every other
              screen narrows the rows already on it (`selectors.ts#tagLensOf`).
            */}
            <div style={{ color: 'var(--gt-muted)', fontSize: 10.5 }}>{v.tags.appliesTxt}</div>
          </div>

          {v.tags.hasPicked ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={LABEL}>PICKED — {v.tags.pickedTitle}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{v.tags.picked.map(tagChip)}</div>
            </div>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={LABEL}>POPULAR</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{v.tags.popular.map(tagChip)}</div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={LABEL}>ALL STEAM TAGS</div>
            <SearchBox
              value={s.tagQ}
              onChange={(e) => s.setTagQ(e.target.value)}
              placeholder={v.tags.searchPlaceholder}
              w="360px"
              pfs={11}
              ifs={12}
              pad="7px 10px"
            />
            {v.tags.hasResults ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{v.tags.results.map(tagChip)}</div>
            ) : null}
            {v.tags.noHit ? (
              <div style={{ color: 'var(--gt-faint)', fontSize: 11 }}>NO STEAM TAG MATCHES THAT</div>
            ) : null}
            {v.tags.vocabDown ? (
              <div
                style={{
                  border: '1px dashed var(--gt-border-2)',
                  color: 'var(--gt-muted)',
                  fontSize: 10,
                  padding: '10px 12px',
                }}
              >
                {v.tags.vocabDownTxt}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* footer ----------------------------------------------------------- */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '13px 18px',
          borderTop: '1px solid var(--gt-line)',
        }}
      >
        {v.hasActive ? (
          <div
            onClick={() => s.clearFlt()}
            style={{
              border: '1px solid var(--gt-border-2)',
              color: 'var(--gt-text-5)',
              fontFamily: MONO,
              fontSize: 12,
              padding: '9px 14px',
              cursor: 'pointer',
            }}
          >
            CLEAR ALL
          </div>
        ) : null}
        <div style={{ flex: 1 }} />
        <div
          onClick={close}
          style={{
            border: '1px solid var(--acc)',
            color: 'var(--acc)',
            background: 'var(--accSoft)',
            fontFamily: MONO,
            fontSize: 12,
            padding: '9px 18px',
            cursor: 'pointer',
          }}
        >
          DONE
        </div>
      </div>
    </Modal>
  );
}

export default FilterBar;
